'use client';

import React from 'react';
import { Clock, EyeOff } from 'lucide-react';
import { IoBaseballOutline } from 'react-icons/io5';

/** Home plate pentagon icon */
function HomePlate({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.2" className={className}>
      <polygon points="1,0.5 11,0.5 11,5 6,9.5 1,5" />
    </svg>
  );
}
import { cn, SportsbookLineCell, BestLineSummary, type LiquidityThresholds } from '@sharpr/ui';
import { TeaseSurface } from '@sharpr/ui/rich-popover';
import { PlayerAvatar } from '../PlayerAvatar';
import { TeamLogo } from '../TeamLogo';
import { NotesPopover } from '../NotesPopover';
import { parseTeamMatchup } from '../../types/table';
import type { PropRow } from '../client/VirtualizedPropTable';
import type { OverlayTarget, ResearchTarget } from '../../types/research';
import { getResearchEdge } from '../../utils/bestEdge';
import { buildOverlayTarget } from '../../utils/buildOverlayTarget';
import { buildResearchTargetFromPropRow } from '@/lib/research/buildResearchTarget';
import {
  isPitcherMarket,
  resolvePositionType,
} from '@/utils/playerTaxonomy';
import { formatActivityMetric } from '@sharpr/types';
import type { BookLine, Sport } from '@sharpr/types';
import { useEvTease } from '../../contexts/EvTeaseContext';
import { WeatherPill } from '../client/weather/WeatherPill';

interface TableRowProps {
  row: PropRow;
  /** Visible book columns (also the user's effective "books I track" set). */
  sportsbooks: string[];
  primaryColWidth: number;
  bestLineColWidth: number;
  sportsbookColWidth: number;
  totalWidth: number;
  virtualRowStart: number;
  virtualRowSize: number;
  onLineMovementClick?: (target: OverlayTarget) => void;
  onOpenResearch?: (target: ResearchTarget) => void;
  /** Told when this row's notes popover opens or closes (useAnchoredRow keeps the row mounted meanwhile). */
  onNotesOpenChange?: (rowId: string, open: boolean) => void;
  sport?: Sport;
  liquidityThresholds?: LiquidityThresholds;
  /** When set (typically `'tour-demo-odds-cell'`), stamps `data-tour` on the
   *  sticky best-line cell so the product walkthrough can spotlight an odds
   *  cell (not the whole row, and not a player card on the left). Sticky =
   *  always visible regardless of horizontal scroll. */
  tourTargetId?: string;
  /** When set (typically `'tour-demo-weather'`), stamps `data-tour` on this row's
   *  weather pill so the walkthrough can spotlight weather on desktop (the mobile
   *  card carries the same target). Only the first row with outdoor weather gets
   *  it, so the spotlight has a single element to measure. */
  tourWeatherTargetId?: string;
  /** Extra class(es) for the row's root element (e.g. demo fade-in hook). */
  className?: string;
}

export const TableRow = React.memo(function TableRow({
  row,
  sportsbooks,
  primaryColWidth,
  bestLineColWidth,
  sportsbookColWidth,
  totalWidth,
  virtualRowStart,
  virtualRowSize,
  onLineMovementClick,
  onOpenResearch,
  onNotesOpenChange,
  sport = 'nba',
  liquidityThresholds,
  tourTargetId,
  tourWeatherTargetId,
  className,
}: TableRowProps) {
  const teaseEv = useEvTease();

  const researchEnabled = !!onOpenResearch;

  const teaseProjection = teaseEv;

  // Group book lines by book name for easy lookup. Memoized so a scroll-position-only
  // re-render (virtualRowStart/Size change on a React.memo row) doesn't rebuild it.
  const booksByName = React.useMemo(() => {
    const acc: Record<string, { over?: BookLine; under?: BookLine }> = {};
    row.books.forEach(line => {
      acc[line.book] = { ...acc[line.book], [line.side]: line };
    });
    return acc;
  }, [row.books]);

  // Books to surface in the Bet/Share popovers: the user's visible columns (`sportsbooks`), as the mobile list does. A
  // reference book or a thin exchange side can be chosen there; the overlay opens on the row's own best line
  // (`row.bestLines`, priced with the viewer's eligibility), so neither is ever the default.
  const pickerBooks = React.useMemo(
    () => row.books.filter((b) => sportsbooks.length === 0 || sportsbooks.includes(b.book)),
    [row.books, sportsbooks],
  );

  return (
    <div
      className={cn(
        "absolute top-0 left-0 w-full flex transition-colors duration-500",
        "hover:bg-muted/30 dark:hover:bg-bg-overlay/50",
        "border-b border-border dark:border-border-secondary",
        row.isUpdated && "bg-brand-50/20 dark:bg-brand-500/10",
        onLineMovementClick && row.projectionInfo.value !== null && "cursor-pointer",
        className
      )}
      style={{
        height: `${virtualRowSize}px`,
        transform: `translateY(${virtualRowStart}px)`,
        width: totalWidth
      }}
      onClick={(e) => {
        if (onLineMovementClick && row.projectionInfo.value !== null) {
          // `data-book` is on each sportsbook column wrapper. Clicking inside a
          // column passes the book name through as `clickedBook` so the
          // LineMovementChart can highlight that book's line. Row chrome /
          // best-line summary clicks produce `undefined`. The row's CTAs
          // (Bet, Share) no longer thread off this — they anchor to the row's
          // best +EV in the overlay.
          const target = e.target as HTMLElement;
          const clickedBook = target.closest('[data-book]')?.getAttribute('data-book') ?? undefined;

          onLineMovementClick(buildOverlayTarget({
            playerName: row.primaryInfo.title,
            market: row.projectionInfo.unit,
            marketCode: row.projectionInfo.market,
            imageUrl: row.primaryInfo.imageUrl,
            playerId: row.playerId,
            projection: row.projectionInfo.value,
            pmf: row.pmf,
            outPlayerIds: row.outPlayerIds,
            bestLines: row.bestLines,
            books: pickerBooks,
            projectedMinutes: row.projectionInfo.minutes,
            clickedBook,
            team: row.team ?? '',
            opponent: row.opponent,
            commenceTime: row.commenceTime,
            gameId: row.gameId,
            isHome: row.isHome,
            weather: row.weather,
            position: row.primaryInfo.position,
            positionType: resolvePositionType(row.primaryInfo.position, row.projectionInfo.market),
            teamNotes: row.teamNotes,
            lineup: row.lineup,
            marketLines: row.marketLines,
            marketProjections: row.marketProjections,
          }));
        }
      }}
    >
      {/* Primary info column - sticky left */}
      <div
        className="sticky left-0 bg-bg dark:bg-bg-primary z-10 border-r border-border dark:border-border-primary px-4 py-0.5 flex items-center"
        style={{ width: primaryColWidth, minWidth: primaryColWidth }}
      >
        <div className="flex items-center gap-2 w-full">
          {/* Player avatar */}
          <PlayerAvatar name={row.primaryInfo.title} imageUrl={row.primaryInfo.imageUrl} size="sm" />

          {/* Text info */}
          <div className="flex-1 min-w-0">
            {/* Primary: Title with Position */}
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={cn(
                  // `prop-row-player-name` = styling hook for the demo click-hint ring.
                  "prop-row-player-name font-medium text-sm text-fg dark:text-text-primary truncate min-w-0",
                  researchEnabled && "hover:text-brand-500 dark:hover:text-brand-400 cursor-pointer transition-colors"
                )}
                onClick={(e) => {
                  if (!researchEnabled || !onOpenResearch) return;
                  e.stopPropagation();
                  onOpenResearch(buildResearchTargetFromPropRow(row));
                }}
                onKeyDown={researchEnabled ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenResearch?.(buildResearchTargetFromPropRow(row));
                  }
                } : undefined}
                role={researchEnabled ? 'button' : undefined}
                tabIndex={researchEnabled ? 0 : undefined}
              >
                {row.primaryInfo.title}
              </span>
              {row.isQuestionable && (
                <span className="inline-flex items-center flex-shrink-0 px-1 py-0.5 text-[10px] font-bold leading-none rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                  Q
                </span>
              )}
              {row.primaryInfo.position && (
                <span className="text-xs text-muted-foreground dark:text-text-tertiary flex-shrink-0 whitespace-nowrap">
                  {row.primaryInfo.position}
                </span>
              )}
              <NotesPopover lineup={row.lineup} teamNotes={row.teamNotes} playerNotes={row.playerNotes} teamLabel={row.team} sport={sport}
                playerName={row.primaryInfo.title} playerImageUrl={row.primaryInfo.imageUrl}
                onOpenChange={open => onNotesOpenChange?.(row.id, open)} />
            </div>

            {/* Secondary: Subtitle with team logos — display-only. Game lines
                now live in the player details overlay (opened by the row click),
                so the matchup is no longer a separate trigger. */}
            {row.primaryInfo.subtitle && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground dark:text-text-secondary leading-tight mt-0.5">
                {(() => {
                  const matchup = parseTeamMatchup(row.primaryInfo.subtitle);
                  if (matchup) {
                    const { team, vsAt, opponent } = matchup;
                    return (
                      <>
                        <span className="inline-flex items-center gap-1.5">
                          <TeamLogo team={team} sport={sport} className="w-4 h-4" />
                          <span>{team}</span>
                          <span className="mx-1">{vsAt}</span>
                          <TeamLogo team={opponent} sport={sport} className="w-4 h-4" />
                          <span>{opponent}</span>
                        </span>
                        {row.weather && (
                          <span data-tour={tourWeatherTargetId} className="inline-flex">
                            <WeatherPill weather={row.weather} className="ml-1 hidden md:inline-flex" />
                          </span>
                        )}
                      </>
                    );
                  }
                  // Fallback: just show the subtitle as-is
                  return (
                    <>
                      <span>{row.primaryInfo.subtitle}</span>
                      {row.weather && (
                        <span data-tour={tourWeatherTargetId} className="inline-flex">
                          <WeatherPill weather={row.weather} className="ml-1 hidden md:inline-flex" />
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Tertiary info */}
            {row.primaryInfo.tertiary && (
              <div className="text-xs text-muted-foreground dark:text-text-tertiary mt-0.5">
                {row.primaryInfo.tertiary}
              </div>
            )}
          </div>

          {/* Projection value (blurred ghost for non-subscribers — the unit/market
              label stays, only the projected number + activity metric are teased,
              and the whole cell becomes a hover-CTA → signup). */}
          {(() => {
            const projInner = (
              <>
                <div className="flex items-baseline gap-1">
                  {!!row.hiddenBy?.length && (
                    <span
                      className="self-center inline-flex flex-shrink-0"
                      title={row.hiddenBy.includes('player')
                        ? 'Analyst-hidden — EV visible to admins only; publishes odds-only to subscribers'
                        : 'EV visible to admins only — stripped for subscribers'}
                    >
                      <EyeOff
                        className={cn('h-3 w-3', row.hiddenBy.includes('player') ? 'text-muted-foreground' : 'text-amber-500')}
                        aria-label={row.hiddenBy.includes('player') ? 'Analyst-hidden market' : 'Admin-only EV'}
                      />
                    </span>
                  )}
                  <span className="font-mono text-xs font-medium text-fg dark:text-text-primary">
                    {row.projectionInfo.value !== null
                      ? row.projectionInfo.value.toFixed(2)
                      : teaseProjection
                        ? <span className="blur-[3px] opacity-50 select-none">0.00</span>
                        : ''}
                  </span>
                  <span className="text-[11px] text-muted-foreground dark:text-text-tertiary uppercase">
                    {row.projectionInfo.unit}
                  </span>
                </div>
                {(row.projectionInfo.minutes != null || teaseProjection) && (
                  <span className={cn(
                    "text-[11px] text-muted-foreground dark:text-text-tertiary flex items-center gap-1",
                    // Non-subs: blur the whole activity row (icon + number), not just the number.
                    row.projectionInfo.minutes == null && "blur-[3px] opacity-50 select-none"
                  )}>
                    {sport === 'mlb'
                      ? (isPitcherMarket(row.projectionInfo.market)
                        ? <IoBaseballOutline size={12} />
                        : <HomePlate className="w-3 h-3" />)
                      : <Clock className="w-3 h-3" />}
                    {row.projectionInfo.minutes != null
                      ? formatActivityMetric(sport, row.projectionInfo.minutes, row.projectionInfo.market)
                      : formatActivityMetric(sport, 0, row.projectionInfo.market)}
                  </span>
                )}
              </>
            );
            return teaseProjection && row.projectionInfo.value === null ? (
              <TeaseSurface
                title="Unlock projections"
                surface="table_projection"
                body="See our model's projected output for every player — refreshed alongside the lines, with EV on every market."
                ctaLabel="Start free trial"
                ctaHref="/signup"
                secondaryLabel="Already have an account? Log in"
                secondaryHref="/login"
                side="top"
                wrapperClassName="flex flex-col items-end flex-shrink-0"
              >
                {projInner}
              </TeaseSurface>
            ) : (
              <div className="flex flex-col items-end flex-shrink-0">{projInner}</div>
            );
          })()}
        </div>
      </div>

      {/* Best Line column - sticky left after primary */}
      <div
        className="sticky bg-bg dark:bg-bg-primary z-10 border-r border-border dark:border-border-primary p-0.5 flex items-center justify-center"
        data-book={getResearchEdge(row.bestLines)?.book}
        data-tour={tourTargetId}
        style={{
          left: primaryColWidth,
          width: bestLineColWidth,
          minWidth: bestLineColWidth
        }}
      >
        {row.bestLines?.over || row.bestLines?.under
          ? <BestLineSummary bestLines={row.bestLines} evVariant={row.inQuestionableGame ? 'questionable' : 'default'} liquidityThresholds={liquidityThresholds} teaseEv={teaseProjection} />
          : <span className="text-xs text-muted-foreground dark:text-text-tertiary">—</span>
        }
      </div>

      {/* Sportsbook columns */}
      {sportsbooks.map((book) => {
        const lines = booksByName[book];
        const overLine = lines?.over;
        const underLine = lines?.under;

        return (
          <div
            key={book}
            data-book={book}
            className="border-r border-border dark:border-border-secondary p-0.5 flex items-center justify-center"
            style={{ width: sportsbookColWidth, minWidth: sportsbookColWidth }}
          >
            <SportsbookLineCell
              lines={{ over: overLine, under: underLine }}
              evVariant={row.inQuestionableGame ? 'questionable' : 'default'}
              liquidityThresholds={liquidityThresholds}
              teaseEv={teaseProjection}
            />
          </div>
        );
      })}
    </div>
  );
});
